// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Login Page
//
// Sign-in only. No account creation here — new org admins are provisioned
// via invite (Path A: org signup request; Path B: platform admin direct invite).
// See /request-access and /activate public routes.

import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, signInWithGoogle, AppError } from '../services/firebase';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      navigate('/dashboard');
    } catch (err) {
      setError((err as AppError).message ?? 'Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      setError((err as AppError).message ?? 'Sign in failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--color-secondary)',
    }}>
      <div style={{ width: 380 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-primary)',
            letterSpacing: -2, marginBottom: 6 }}>Ap</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
            Aphilaty Admin
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            Organization management portal
          </div>
        </div>

        {/* Form card */}
        <div style={{ background: 'var(--color-bg)', borderRadius: 'var(--radius-lg)', padding: 28 }}>
          {error && (
            <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="email">Email</label>
              <input id="email" type="email" className="form-input"
                value={email} onChange={e => setEmail(e.target.value)}
                autoComplete="email" autoFocus required
                placeholder="you@example.com" />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input id="password" type="password" className="form-input"
                value={password} onChange={e => setPassword(e.target.value)}
                autoComplete="current-password" required placeholder="••••••••" />
            </div>

            <button type="submit" className="btn btn-primary"
              style={{ width: '100%', marginTop: 8 }}
              disabled={isLoading}>
              {isLoading ? <span className="spinner" /> : 'Sign In'}
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            margin: '16px 0', color: 'var(--color-text-tertiary)', fontSize: 12 }}>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border)' }} />
            or
            <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border)' }} />
          </div>

          <button onClick={handleGoogle} className="btn btn-outline"
            style={{ width: '100%' }} disabled={isLoading}>
            Sign in with Google
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12,
          color: 'rgba(255,255,255,0.5)' }}>
          Don't have access?{' '}
          <Link to="/request-access"
            style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
            Request access for your organization →
          </Link>
        </p>

        <p style={{ textAlign: 'center', marginTop: 8,
          fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
          © {new Date().getFullYear()} Aphilaty. All rights reserved.
        </p>
      </div>
    </div>
  );
}
